import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { ref, onValue } from 'firebase/database';
import { database, auth } from '../firebase/firebaseConfig';
import { Card, DashboardGrid, StyledList, StyledListItem, WidgetListItem, CardContent, Button, SplitButtonLeft, SplitButtonRight, SplitButtonGroup, Dropdown, DropdownContent, Select, ModalContainer, ModalContent, Input, InputWrapper, ClearButton } from './StyledComponents';
import { Line, Bar } from 'react-chartjs-2';
import 'chart.js/auto';
import EditTransactionModal from './EditTransactionModal';
import DeleteTransactionModal from './DeleteTransactionModal';
import PartialPayModal from './PartialPayModal';
import AddTransactionModal from './AddTransactionModal';
import EditAccountModal from './EditAccountModal';
import ReconciliationWidget from './ReconciliationWidget';
import BugReportsWidget from './BugReportsWidget';
import { createTransactionWithRecurrence, saveTransactionEdits } from '../utils/transactionHelpers';
import {
  addCalendarMonths,
  buildCashflowProjection,
  calculateAgeOnDate,
  formatTimeUntilDate,
} from '../utils/cashflowProjection';
import {
  markTransactionPaid,
  updateTransaction,
  deleteTransaction,
  deleteRecurringTransaction,
  partialPayTransaction,
  skipRecurringTransactionOccurrence,
  updateAccount,
  markReconciliationTransaction,
  markTransactionReconciliationMatch,
  updateUserSettings,
} from '../firebase/firebaseUtils';

const TRANSACTION_WIDGET_PAGE_SIZE = 30;
const CASHFLOW_ACCOUNT_TYPES = ['Bank', 'Loan', 'Asset'];
const SHORTFALL_ACCOUNT_TYPES = ['Bank', 'Asset'];

const normalizeAmountSearch = (value) => value.replace(/[$,\s]/g, '');

const getReconciliationMatches = (transaction) => (
  transaction?.reconciliationMatches && typeof transaction.reconciliationMatches === 'object'
    ? transaction.reconciliationMatches
    : {}
);

const hasReconciliationMatch = (transaction, accountId) => {
  if (!accountId) return false;
  const match = getReconciliationMatches(transaction)[accountId];
  return Boolean(match?.reconciliationTransactionId || match?.reconciledAt || match === true);
};

const getSelectedTransferAccountIds = (transaction, selectedAccountSet) => {
  if (!transaction?.fromAccount || !transaction?.toAccount) return [];
  if (transaction.fromAccount === transaction.toAccount) return [];
  return [transaction.fromAccount, transaction.toAccount].filter((accountId) => (
    selectedAccountSet.has(accountId)
  ));
};

const isTransferAwaitingReconciliation = (transaction, selectedAccountSet) => {
  if (!transaction?.isPaid) return false;
  const transferAccountIds = getSelectedTransferAccountIds(transaction, selectedAccountSet);
  if (transferAccountIds.length < 2) return false;
  const matchedCount = transferAccountIds.filter((accountId) => (
    hasReconciliationMatch(transaction, accountId)
  )).length;
  return matchedCount > 0 && matchedCount < transferAccountIds.length;
};

const canReconcilePaidTransferSide = (transaction, recon, selectedAccountSet) => {
  if (!transaction?.isPaid || !recon?.accountId) return false;
  const transferAccountIds = getSelectedTransferAccountIds(transaction, selectedAccountSet);
  return (
    transferAccountIds.length >= 2 &&
    transferAccountIds.includes(recon.accountId) &&
    !hasReconciliationMatch(transaction, recon.accountId)
  );
};

const Overview = ({
  userId,
  settings,
  settingsLoaded,
  onSettingsUpdate,
  setCustomizeWidgetsHandler,
  isAdmin,
  birthDate,
}) => {
  const shallowEqual = (a = {}, b = {}) => {
    const aKeys = Object.keys(a || {});
    const bKeys = Object.keys(b || {});
    if (aKeys.length !== bKeys.length) {
      return false;
    }
    return aKeys.every((key) => Object.is(a?.[key], b?.[key]));
  };

  const [transactions, setTransactions] = useState([]);
  const [accounts, setAccounts] = useState({});
  const [selectedReconciliationAccounts, setSelectedReconciliationAccounts] = useState({});
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [isEditAccountModalOpen, setIsEditAccountModalOpen] = useState(false);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletingTransaction, setDeletingTransaction] = useState(null);
  const [isPartialPayModalOpen, setIsPartialPayModalOpen] = useState(false);
  const [isAddTransactionModalOpen, setIsAddTransactionModalOpen] = useState(false);
  const [addTransactionPrefill, setAddTransactionPrefill] = useState(null);
  const [isAddTransactionButtonDragOver, setIsAddTransactionButtonDragOver] = useState(false);
  const [partialPayTransactionData, setPartialPayTransactionData] = useState(null);
  const [pendingReconPartialPayment, setPendingReconPartialPayment] = useState(null);
  const [isCashRunwayModalOpen, setIsCashRunwayModalOpen] = useState(false);
  const [dragHoverTransactionId, setDragHoverTransactionId] = useState(null);
  const [reconDropContext, setReconDropContext] = useState(null);
  const [reconProcessing, setReconProcessing] = useState(false);
  const [transactionFilterText, setTransactionFilterText] = useState('');
  const [visibleTransactionCount, setVisibleTransactionCount] = useState(
    TRANSACTION_WIDGET_PAGE_SIZE
  );
  const dropdownRefs = useRef({});
  const transactionListRef = useRef(null);
  const transactionLoadMoreRef = useRef(null);
  const closeDropdown = () => setOpenDropdownId(null);

  const [expenseAccountFilter, setExpenseAccountFilter] = useState(
    () => settings?.expenseAccountFilter || {}
  );
  const [incomeAccountFilter, setIncomeAccountFilter] = useState(
    () => settings?.incomeAccountFilter || {}
  );
  const [incomeExpenseAccountFilter, setIncomeExpenseAccountFilter] = useState(
    () => settings?.incomeExpenseAccountFilter || {}
  );
  const [cashflowAccountFilter, setCashflowAccountFilter] = useState(
    () => settings?.cashflowAccountFilter || {}
  );
  const [cashShortfallAccountFilter, setCashShortfallAccountFilter] = useState(
    () => settings?.cashShortfallAccountFilter || {}
  );
  const [balanceAccountFilter, setBalanceAccountFilter] = useState(
    () => settings?.balanceAccountFilter || {}
  );
  const [cashflowPeriod, setCashflowPeriod] = useState(
    () => settings?.cashflowPeriod || 3
  );
  const cashflowChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      tooltip: { mode: 'index', intersect: false, enabled: true },
      legend: { display: true },
    },
    elements: {
      point: { radius: 0, hoverRadius: 0, hitRadius: 10 },
    },
  };

  const persistSetting = useCallback(
    (key, value) => {
      if (!userId) {
        return;
      }
      updateUserSettings(userId, { [key]: value }).catch((error) => {
        console.error(`Failed to persist ${key} to user settings`, error);
      });
      if (onSettingsUpdate) {
        onSettingsUpdate({ [key]: value });
      }
    },
    [userId, onSettingsUpdate]
  );

  const settingsExpenseFilter = settings?.expenseAccountFilter;
  const settingsIncomeFilter = settings?.incomeAccountFilter;
  const settingsIncomeExpenseFilter = settings?.incomeExpenseAccountFilter;
  const settingsCashflowFilter = settings?.cashflowAccountFilter;
  const settingsCashflowPeriod = settings?.cashflowPeriod;
  const settingsCashShortfallFilter = settings?.cashShortfallAccountFilter;
  const settingsBalanceFilter = settings?.balanceAccountFilter;
  const [filtersHydrated, setFiltersHydrated] = useState(false);
  const filtersReadyRef = useRef(false);

  useEffect(() => {
    if (!settingsLoaded) {
      setFiltersHydrated(false);
      filtersReadyRef.current = false;
      return;
    }

    setFiltersHydrated(false);
    filtersReadyRef.current = false;

    setExpenseAccountFilter((prev) => {
      const target = settingsExpenseFilter || {};
      return shallowEqual(prev, target) ? prev : target;
    });
    setIncomeAccountFilter((prev) => {
      const target = settingsIncomeFilter || {};
      return shallowEqual(prev, target) ? prev : target;
    });
    setIncomeExpenseAccountFilter((prev) => {
      const target = settingsIncomeExpenseFilter || {};
      return shallowEqual(prev, target) ? prev : target;
    });
    setCashflowAccountFilter((prev) => {
      const target = settingsCashflowFilter || {};
      return shallowEqual(prev, target) ? prev : target;
    });
    setCashflowPeriod(settingsCashflowPeriod || 3);
    setCashShortfallAccountFilter((prev) => {
      const target = settingsCashShortfallFilter || {};
      return shallowEqual(prev, target) ? prev : target;
    });
    setBalanceAccountFilter((prev) => {
      const target = settingsBalanceFilter || {};
      return shallowEqual(prev, target) ? prev : target;
    });

    Promise.resolve().then(() => {
      setFiltersHydrated(true);
    });
  }, [
    settingsLoaded,
    settingsExpenseFilter,
    settingsIncomeFilter,
    settingsIncomeExpenseFilter,
    settingsCashflowFilter,
    settingsCashflowPeriod,
    settingsCashShortfallFilter,
    settingsBalanceFilter,
  ]);

  const activeAccountEntries = useMemo(
    () =>
      Object.entries(accounts).filter(([, acc]) => acc && acc.archived !== true),
    [accounts]
  );

  const defaultWidgets = [
    { id: 'transactions', enabled: true, width: 2, height: 1 },
    { id: 'reconciliation', enabled: true, width: 2, height: 1 },
    { id: 'cashRunway', enabled: true, width: 1, height: 1 },
    { id: 'cashflow', enabled: true, width: 2, height: 1 },
    { id: 'expenses', enabled: true, width: 1, height: 1 },
    { id: 'income', enabled: true, width: 1, height: 1 },
    { id: 'incomeExpense', enabled: true, width: 2, height: 1 },
    { id: 'accountBalances', enabled: true, width: 1, height: 1 },
    { id: 'bugReports', enabled: true, width: 1, height: 1 },
  ];

  const [widgets, setWidgets] = useState(() => {
    const saved = localStorage.getItem('overviewWidgets');
    if (saved) {
      try {
        const parsed = JSON.parse(saved).map((w) =>
          w.id === 'balances' || w.id === 'balance'
            ? { ...w, id: 'accountBalances' }
            : w
        );
        const ensured = [...parsed];
        if (!ensured.some((w) => w.id === 'accountBalances')) {
          ensured.push({ id: 'accountBalances', enabled: true, width: 1, height: 1 });
        }
        if (!ensured.some((w) => w.id === 'reconciliation')) {
          ensured.splice(1, 0, { id: 'reconciliation', enabled: true, width: 2, height: 1 });
        }
        if (!ensured.some((w) => w.id === 'cashRunway')) {
          const insertIndex = Math.min(ensured.length, 2);
          ensured.splice(insertIndex, 0, { id: 'cashRunway', enabled: true, width: 1, height: 1 });
        }
        if (!ensured.some((w) => w.id === 'bugReports')) {
          ensured.push({ id: 'bugReports', enabled: true, width: 1, height: 1 });
        }
        return ensured;
      } catch {
        return defaultWidgets;
      }
    }
    return defaultWidgets;
  });

  useEffect(() => {
    localStorage.setItem('overviewWidgets', JSON.stringify(widgets));
  }, [widgets]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!openDropdownId) return;
      const dropdownEl = dropdownRefs.current[openDropdownId];
      if (dropdownEl && dropdownEl.contains(event.target)) {
        return;
      }
      setOpenDropdownId(null);
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [openDropdownId]);

  useEffect(() => {
    setVisibleTransactionCount(TRANSACTION_WIDGET_PAGE_SIZE);
  }, [transactionFilterText, transactions]);

  const toggleWidget = (id) => {
    setWidgets((prev) =>
      prev.map((w) => (w.id === id ? { ...w, enabled: !w.enabled } : w))
    );
  };

  const moveWidget = (index, direction) => {
    setWidgets((prev) => {
      const newWidgets = [...prev];
      const newIndex = index + direction;
      if (newIndex < 0 || newIndex >= newWidgets.length) return prev;
      const [moved] = newWidgets.splice(index, 1);
      newWidgets.splice(newIndex, 0, moved);
      return newWidgets;
    });
  };

  const updateWidgetSize = (id, dimension, value) => {
    setWidgets((prev) =>
      prev.map((w) => (w.id === id ? { ...w, [dimension]: value } : w))
    );
  };

  const toggleDropdown = (id) => {
    setOpenDropdownId((prev) => (prev === id ? null : id));
  };

  const [openAccountDropdown, setOpenAccountDropdown] = useState(null);
  const toggleAccountDropdown = (id) => {
    setOpenAccountDropdown((prev) => (prev === id ? null : id));
  };

  const handleEditAccount = (account) => {
    setEditingAccount(account);
    setIsEditAccountModalOpen(true);
    setOpenAccountDropdown(null);
  };

  const handleSaveAccount = async (updatedAccount) => {
    const userId = auth.currentUser?.uid;
    if (!userId) return;
    const { id, name, balance, type, color, availabilityType, availableFromAge, availableFromDate } = updatedAccount;
    try {
      await updateAccount(userId, id, {
        name,
        balance: typeof balance === 'number' ? balance : parseFloat(balance),
        type,
        color,
        availabilityType,
        availableFromAge,
        availableFromDate,
      });
    } catch (error) {
      console.error('Error saving account: ', error);
    } finally {
      setIsEditAccountModalOpen(false);
      setEditingAccount(null);
    }
  };

  const handleCloseAccountModal = () => {
    setIsEditAccountModalOpen(false);
    setEditingAccount(null);
  };

  const handleMarkPaid = async (transaction) => {
    const userId = auth.currentUser?.uid;
    if (!userId) return;
    const { id, parentId, fromAccount, toAccount, amount } = transaction;
    try {
      await markTransactionPaid(userId, id, parentId, fromAccount, toAccount, amount);
    } catch (error) {
      console.error('Error marking transaction paid: ', error);
    } finally {
      closeDropdown();
    }
  };

  const handleEditTransaction = (transaction) => {
    setEditingTransaction(transaction);
    setIsEditModalOpen(true);
    setOpenDropdownId(null);
  };

  const handleSaveTransaction = async (updated) => {
    const userId = auth.currentUser?.uid;
    if (!userId) return;
    try {
      await saveTransactionEdits(userId, updated);
      setIsEditModalOpen(false);
      setEditingTransaction(null);
    } catch (error) {
      console.error('Error saving transaction: ', error);
    }
  };

  const handlePartialPayClick = (transaction) => {
    setPendingReconPartialPayment(null);
    setPartialPayTransactionData(transaction);
    setIsPartialPayModalOpen(true);
    setOpenDropdownId(null);
  };

  const closePartialPayModal = () => {
    setIsPartialPayModalOpen(false);
    setPartialPayTransactionData(null);
    setPendingReconPartialPayment(null);
  };

  const handlePartialPay = async (amount, date, description) => {
    const userId = auth.currentUser?.uid;
    if (!userId || !partialPayTransactionData) return;
    const { id, parentId, fromAccount, toAccount } = partialPayTransactionData;
    const reconContext = pendingReconPartialPayment;
    try {
      await partialPayTransaction(
        userId,
        id,
        parentId,
        fromAccount,
        toAccount,
        amount,
        date,
        description,
      );
      if (reconContext?.reconId) {
        if (reconContext.accountId) {
          await markTransactionReconciliationMatch(
            userId,
            id,
            parentId,
            reconContext.accountId,
            reconContext.reconId,
            true,
          );
        }
        await markReconciliationTransaction(userId, reconContext.reconId, true);
      }
    } catch (error) {
      console.error('Error partially paying transaction: ', error);
    } finally {
      closePartialPayModal();
    }
  };

  const closeAddTransactionModal = () => {
    setIsAddTransactionModalOpen(false);
    setAddTransactionPrefill(null);
  };

  const openAddTransactionModal = (prefill = null) => {
    setAddTransactionPrefill(prefill);
    setIsAddTransactionModalOpen(true);
  };

  const supportsReconciliationDrop = (event) => {
    if (!event?.dataTransfer?.types) return false;
    const types = Array.from(event.dataTransfer.types);
    return types.includes('application/x-reconciliation-transaction') || types.includes('application/json');
  };

  const parseReconciliationDropPayload = (event) => {
    if (!event?.dataTransfer) return null;
    let raw = event.dataTransfer.getData('application/x-reconciliation-transaction');
    if (!raw) {
      raw = event.dataTransfer.getData('application/json');
    }
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw);
      if (parsed?.type === 'reconciliation-transaction') {
        return parsed;
      }
    } catch (error) {
      console.warn('Unable to parse reconciliation payload for add transaction drop', error);
    }
    return null;
  };

  const sanitizeReconDropDate = (value) => {
    const today = new Date().toISOString().split('T')[0];
    if (!value && value !== 0) return today;
    if (value instanceof Date) {
      return value.toISOString().split('T')[0];
    }
    if (typeof value === 'string') {
      const match = value.match(/\d{4}-\d{2}-\d{2}/);
      if (match) return match[0];
    }
    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toISOString().split('T')[0];
    }
    return today;
  };

  const handleAddTransactionButtonDrop = (event) => {
    setIsAddTransactionButtonDragOver(false);
    if (!supportsReconciliationDrop(event)) return;
    event.preventDefault();
    event.stopPropagation();
    const payload = parseReconciliationDropPayload(event);
    if (!payload) return;

    const numericAmount = Number(payload.amount);
    const hasAmount = Number.isFinite(numericAmount);

    const prefill = {
      description: payload.description || payload.rawDescription || payload.accountName || '',
      amount: hasAmount ? numericAmount : '',
      date: sanitizeReconDropDate(payload.date),
      fromAccount: hasAmount && numericAmount < 0 ? (payload.accountId || '') : '',
      toAccount: hasAmount && numericAmount > 0 ? (payload.accountId || '') : '',
      rawDescription: payload.rawDescription || '',
    };

    openAddTransactionModal(prefill);
  };

  const handleAddTransactionButtonDragOver = (event) => {
    if (!supportsReconciliationDrop(event)) return;
    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'copy';
    }
    setIsAddTransactionButtonDragOver(true);
  };

  const handleAddTransactionButtonDragLeave = () => {
    setIsAddTransactionButtonDragOver(false);
  };

  const handleAddTransactionButtonClick = () => {
    openAddTransactionModal(null);
  };

  const handleAddTransaction = async (newTransaction) => {
    const userId = auth.currentUser?.uid;
    if (!userId) return;
    try {
      await createTransactionWithRecurrence(userId, newTransaction);
    } catch (error) {
      console.error('Error adding transaction: ', error);
    }
  };

  const handleSkipTransaction = async (transaction) => {
    const userId = auth.currentUser?.uid;
    const { id, parentId } = transaction;
    if (!userId || !parentId) return;
    try {
      await skipRecurringTransactionOccurrence(userId, id, parentId);
    } catch (error) {
      console.error('Error skipping transaction: ', error);
    } finally {
      setOpenDropdownId(null);
    }
  };

  const handleReconciliationDragOver = (event, transactionId) => {
    if (!event.dataTransfer) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
    setDragHoverTransactionId(transactionId);
  };

  const handleReconciliationDragLeave = (event, transactionId) => {
    event.preventDefault();
    setDragHoverTransactionId((prev) => (prev === transactionId ? null : prev));
  };

  const handleReconciliationDrop = (event, transaction) => {
    if (!event.dataTransfer) return;
    event.preventDefault();
    event.stopPropagation();
    setDragHoverTransactionId(null);
    let payload = event.dataTransfer.getData('application/x-reconciliation-transaction');
    if (!payload) {
      payload = event.dataTransfer.getData('application/json');
    }
    if (!payload) return;
    let reconData;
    try {
      reconData = JSON.parse(payload);
    } catch (error) {
      console.warn('Failed to parse dropped reconciliation payload', error);
      return;
    }
    if (!reconData || reconData.type !== 'reconciliation-transaction') {
      return;
    }
    processReconciliationDrop(transaction, reconData);
  };

  const recordReconciliationMatch = async (userId, transaction, recon) => {
    if (!recon?.accountId) return;
    await markTransactionReconciliationMatch(
      userId,
      transaction.id,
      transaction.parentId,
      recon.accountId,
      recon.id,
      true,
    );
  };

  const completeExactMatch = async (transaction, recon, reconAbs) => {
    const userId = auth.currentUser?.uid;
    if (!userId) return;
    try {
      setReconProcessing(true);
      if (!transaction.isPaid) {
        await markTransactionPaid(
          userId,
          transaction.id,
          transaction.parentId,
          transaction.fromAccount,
          transaction.toAccount,
          reconAbs,
        );
      }
      await recordReconciliationMatch(userId, transaction, recon);
      await markReconciliationTransaction(userId, recon.id, true);
    } catch (error) {
      console.error('Failed to reconcile transaction', error);
    } finally {
      setReconProcessing(false);
    }
  };

  const findFirstUnpaidRecurringOccurrence = (transaction) => {
    if (!transaction?.parentId) return null;
    return transactions
      .filter((candidate) => (
        candidate.parentId === transaction.parentId && !candidate.isPaid
      ))
      .sort((a, b) => {
        const dateDiff = new Date(a.date) - new Date(b.date);
        if (dateDiff !== 0) return dateDiff;
        return String(a.id).localeCompare(String(b.id));
      })[0] || null;
  };

  const processReconciliationAmountDrop = (transaction, recon) => {
    const reconAmount = Number(recon.amount);
    const transactionAmount = Number(transaction.amount);
    if (!Number.isFinite(reconAmount) || !Number.isFinite(transactionAmount)) {
      return;
    }
    const reconAbs = Math.abs(reconAmount);
    const transactionAbs = Math.abs(transactionAmount);
    const tolerance = 0.005;

    if (Math.abs(reconAbs - transactionAbs) <= tolerance) {
      if (
        transaction.isPaid &&
        !canReconcilePaidTransferSide(transaction, recon, selectedReconciliationAccountSet)
      ) {
        return;
      }
      completeExactMatch(transaction, recon, reconAbs);
      return;
    }

    if (transaction.isPaid) {
      return;
    }

    if (reconAbs < transactionAbs - tolerance) {
      setReconDropContext({
        mode: 'partial',
        transaction,
        recon,
        reconAbs,
        transactionAbs,
      });
      return;
    }

    setReconDropContext({
      mode: 'adjust',
      transaction,
      recon,
      reconAbs,
      transactionAbs,
    });
  };

  const processReconciliationDrop = (transaction, recon) => {
    if (canReconcilePaidTransferSide(transaction, recon, selectedReconciliationAccountSet)) {
      processReconciliationAmountDrop(transaction, recon);
      return;
    }

    const firstUnpaidOccurrence = findFirstUnpaidRecurringOccurrence(transaction);
    if (firstUnpaidOccurrence && firstUnpaidOccurrence.id !== transaction.id) {
      setReconDropContext({
        mode: 'recurringOccurrence',
        transaction,
        firstUnpaidOccurrence,
        recon,
      });
      return;
    }

    processReconciliationAmountDrop(transaction, recon);
  };

  const clearReconDropContext = () => {
    setReconDropContext(null);
    setReconProcessing(false);
  };

  const applyReconPartialPayment = () => {
    if (!reconDropContext) return;
    const { transaction, recon, reconAbs } = reconDropContext;
    const paymentDate = recon.date || new Date().toISOString().split('T')[0];
    setPendingReconPartialPayment({
      reconId: recon.id,
      accountId: recon.accountId,
      defaultAmount: reconAbs,
      defaultDate: paymentDate,
    });
    setPartialPayTransactionData(transaction);
    setIsPartialPayModalOpen(true);
    clearReconDropContext();
  };

  const adjustTransactionToRecon = async () => {
    if (!reconDropContext) return;
    const userId = auth.currentUser?.uid;
    if (!userId) return;
    const { transaction, recon, reconAbs } = reconDropContext;
    try {
      setReconProcessing(true);
      const signedAmount = transaction.amount >= 0 ? reconAbs : -reconAbs;
      await updateTransaction(userId, transaction.id, { amount: signedAmount }, transaction.parentId);
      await markTransactionPaid(
        userId,
        transaction.id,
        transaction.parentId,
        transaction.fromAccount,
        transaction.toAccount,
        Math.abs(signedAmount),
      );
      await recordReconciliationMatch(userId, transaction, recon);
      await markReconciliationTransaction(userId, recon.id, true);
      clearReconDropContext();
    } catch (error) {
      console.error('Failed to adjust transaction to reconciliation', error);
      setReconProcessing(false);
    }
  };

  const handleReconDropAction = (action) => {
    if (!reconDropContext) return;
    if (action === 'cancel') {
      clearReconDropContext();
      return;
    }
    if (action === 'partial') {
      applyReconPartialPayment();
      return;
    }
    if (action === 'adjust') {
      adjustTransactionToRecon();
    }
  };

  const handleRecurringOccurrenceDropAction = (action) => {
    if (!reconDropContext) return;
    if (action === 'cancel') {
      clearReconDropContext();
      return;
    }

    const { transaction, firstUnpaidOccurrence, recon } = reconDropContext;
    clearReconDropContext();
    processReconciliationAmountDrop(
      action === 'firstUnpaid' ? firstUnpaidOccurrence : transaction,
      recon,
    );
  };

  const renderReconciliationListItem = (transaction) => {
    const isHover = dragHoverTransactionId === transaction.id;
    const awaitingReconciliation = isTransferAwaitingReconciliation(
      transaction,
      selectedReconciliationAccountSet,
    );
    const missingReconciliationAccounts = awaitingReconciliation
      ? getSelectedTransferAccountIds(transaction, selectedReconciliationAccountSet)
          .filter((accountId) => !hasReconciliationMatch(transaction, accountId))
          .map((accountId) => accounts[accountId]?.name)
          .filter(Boolean)
      : [];
    return (
      <StyledListItem
        key={transaction.id}
        $clickable
        onClick={() => handleEditTransaction(transaction)}
        onDragOver={(e) => handleReconciliationDragOver(e, transaction.id)}
        onDragEnter={(e) => handleReconciliationDragOver(e, transaction.id)}
        onDragLeave={(e) => handleReconciliationDragLeave(e, transaction.id)}
        onDrop={(e) => handleReconciliationDrop(e, transaction)}
        style={{
          backgroundColor: isHover ? 'rgba(76, 175, 80, 0.15)' : undefined,
          transition: 'background-color 0.15s ease-in-out',
        }}
      >
        <span>
          {transaction.description} - {formatCurrency(transaction.amount)} on {formatDate(transaction.date)} from {accounts[transaction.fromAccount]?.name}
          {missingReconciliationAccounts.length > 0
            ? ` (awaiting ${missingReconciliationAccounts.join(', ')})`
            : ''}
        </span>
        <SplitButtonGroup onClick={(e) => e.stopPropagation()}>
          <SplitButtonLeft onClick={(e) => { e.stopPropagation(); handleMarkPaid(transaction); }}>Mark Paid</SplitButtonLeft>
          <Dropdown
            ref={(el) => {
              if (el) {
                dropdownRefs.current[transaction.id] = el;
              } else {
                delete dropdownRefs.current[transaction.id];
              }
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <SplitButtonRight onClick={(e) => { e.stopPropagation(); toggleDropdown(transaction.id); }}>▾</SplitButtonRight>
            {openDropdownId === transaction.id && (
              <DropdownContent onClick={(e) => e.stopPropagation()}>
                <button onClick={(e) => { e.stopPropagation(); handlePartialPayClick(transaction); }}>Partial Pay</button>
                {transaction.parentId && (
                  <button onClick={(e) => { e.stopPropagation(); handleSkipTransaction(transaction); }}>Skip</button>
                )}
                <button onClick={(e) => { e.stopPropagation(); handleEditTransaction(transaction); }}>Edit</button>
                <button onClick={(e) => { e.stopPropagation(); handleDeleteTransactionClick(transaction); }}>Delete</button>
              </DropdownContent>
            )}
          </Dropdown>
        </SplitButtonGroup>
      </StyledListItem>
    );
  };

  const handleDeleteTransactionClick = (transaction) => {
    setDeletingTransaction(transaction);
    setIsDeleteModalOpen(true);
    setOpenDropdownId(null);
  };

  const handleDeleteTransaction = async (deleteMode) => {
    const userId = auth.currentUser?.uid;
    if (!userId || !deletingTransaction) return;
    const { id, parentId } = deletingTransaction;
    try {
      if (deleteMode === 'single' || !parentId) {
        await deleteTransaction(userId, id, parentId);
      } else if (deleteMode === 'series' && parentId) {
        await deleteRecurringTransaction(userId, parentId);
      }
    } catch (error) {
      console.error('Error deleting transaction: ', error);
    } finally {
      setDeletingTransaction(null);
      setIsDeleteModalOpen(false);
    }
  };

  const toggleExpenseAccount = (id) => {
    setExpenseAccountFilter((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      persistSetting('expenseAccountFilter', next);
      return next;
    });
  };

  const toggleIncomeAccount = (id) => {
    setIncomeAccountFilter((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      persistSetting('incomeAccountFilter', next);
      return next;
    });
  };

  const toggleIncomeExpenseAccount = (id) => {
    setIncomeExpenseAccountFilter((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      persistSetting('incomeExpenseAccountFilter', next);
      return next;
    });
  };

  const toggleCashflowAccount = (id) => {
    setCashflowAccountFilter((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      persistSetting('cashflowAccountFilter', next);
      return next;
    });
  };

  const toggleCashShortfallAccount = (id) => {
    setCashShortfallAccountFilter((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      persistSetting('cashShortfallAccountFilter', next);
      return next;
    });
  };


  const toggleBalanceAccount = (id) => {
    setBalanceAccountFilter((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      persistSetting('balanceAccountFilter', next);
      return next;
    });
  };

  const widgetNames = {
    transactions: 'Transactions',
    reconciliation: 'Reconciliation',
    cashflow: 'Cashflow',
    cashRunway: 'Projected Cash Shortfall',
    expenses: 'Expenses',
    income: 'Income',
    incomeExpense: 'Income & Expenses',
    accountBalances: 'Account Balances',
    bugReports: 'Bug Reports',
  };

  const [showConfig, setShowConfig] = useState(false);

  useEffect(() => {
    if (setCustomizeWidgetsHandler) {
      setCustomizeWidgetsHandler(() => () => setShowConfig(true));
      return () => setCustomizeWidgetsHandler(null);
    }
  }, [setCustomizeWidgetsHandler]);

  useEffect(() => {
    if (!auth.currentUser) return;
    const userId = auth.currentUser.uid;

    const transactionsRef = ref(database, `users/${userId}/transactions`);
    onValue(transactionsRef, (snapshot) => {
      const data = snapshot.val() || {};
      const all = [];
      Object.entries(data).forEach(([key, value]) => {
        if (value.childTransactions) {
          Object.entries(value.childTransactions).forEach(([childKey, childValue]) => {
            all.push({ id: childKey, parentId: key, ...childValue });
          });
        } else {
          all.push({ id: key, ...value });
        }
      });
      all.sort((a, b) => {
        const dateDiff = new Date(a.date) - new Date(b.date);
        if (dateDiff !== 0) return dateDiff;
        return b.amount - a.amount;
      });
      setTransactions(all);
    });

    const accountsRef = ref(database, `users/${userId}/accounts`);
    onValue(accountsRef, (snapshot) => {
      setAccounts(snapshot.val() || {});
    });

    const reconciliationSettingsRef = ref(database, `users/${userId}/reconciliation/settings`);
    onValue(reconciliationSettingsRef, (snapshot) => {
      const data = snapshot.val();
      setSelectedReconciliationAccounts(data?.selectedAccounts || {});
    });
  }, []);

  useEffect(() => {
    if (!settingsLoaded || !filtersHydrated) {
      return;
    }
    if (!filtersReadyRef.current) {
      filtersReadyRef.current = true;
      return;
    }
    setExpenseAccountFilter((prev) => {
      const base = settingsExpenseFilter ?? prev;
      const expense = {};
      activeAccountEntries.forEach(([id, acc]) => {
        if (acc.type === 'Expense') {
          const existing = base?.[id];
          expense[id] = existing === undefined ? true : existing;
        }
      });
      if (shallowEqual(prev, expense)) {
        return prev;
      }
      const shouldPersist =
        settingsExpenseFilter === undefined ||
        !shallowEqual(settingsExpenseFilter, expense);
      if (shouldPersist) {
        persistSetting('expenseAccountFilter', expense);
      }
      return expense;
    });
    setIncomeAccountFilter((prev) => {
      const base = settingsIncomeFilter ?? prev;
      const income = {};
      activeAccountEntries.forEach(([id, acc]) => {
        if (acc.type === 'Income') {
          const existing = base?.[id];
          income[id] = existing === undefined ? true : existing;
        }
      });
      if (shallowEqual(prev, income)) {
        return prev;
      }
      const shouldPersist =
        settingsIncomeFilter === undefined ||
        !shallowEqual(settingsIncomeFilter, income);
      if (shouldPersist) {
        persistSetting('incomeAccountFilter', income);
      }
      return income;
    });
    setIncomeExpenseAccountFilter((prev) => {
      const base = settingsIncomeExpenseFilter ?? prev;
      const ie = {};
      activeAccountEntries.forEach(([id, acc]) => {
        if (acc.type === 'Income' || acc.type === 'Expense') {
          const existing = base?.[id];
          ie[id] = existing === undefined ? true : existing;
        }
      });
      if (shallowEqual(prev, ie)) {
        return prev;
      }
      const shouldPersist =
        settingsIncomeExpenseFilter === undefined ||
        !shallowEqual(settingsIncomeExpenseFilter, ie);
      if (shouldPersist) {
        persistSetting('incomeExpenseAccountFilter', ie);
      }
      return ie;
    });
    setCashflowAccountFilter((prev) => {
      const base = settingsCashflowFilter ?? prev;
      const cashflow = {};
      activeAccountEntries.forEach(([id, acc]) => {
        if (['Bank', 'Loan', 'Asset'].includes(acc.type)) {
          const existing = base?.[id];
          if (existing === undefined) {
            cashflow[id] = acc.type === 'Bank';
          } else {
            cashflow[id] = existing;
          }
        }
      });
      if (shallowEqual(prev, cashflow)) {
        return prev;
      }
      const shouldPersist =
        settingsCashflowFilter === undefined ||
        !shallowEqual(settingsCashflowFilter, cashflow);
      if (shouldPersist) {
        persistSetting('cashflowAccountFilter', cashflow);
      }
      return cashflow;
    });
    setCashShortfallAccountFilter((prev) => {
      const base = settingsCashShortfallFilter ?? prev;
      const shortfall = {};
      activeAccountEntries.forEach(([id, acc]) => {
        if (SHORTFALL_ACCOUNT_TYPES.includes(acc.type)) {
          const existing = base?.[id];
          shortfall[id] = existing === undefined ? acc.type === 'Bank' : existing;
        }
      });
      if (shallowEqual(prev, shortfall)) return prev;
      const shouldPersist =
        settingsCashShortfallFilter === undefined ||
        !shallowEqual(settingsCashShortfallFilter, shortfall);
      if (shouldPersist) persistSetting('cashShortfallAccountFilter', shortfall);
      return shortfall;
    });
    setBalanceAccountFilter((prev) => {
      const base = settingsBalanceFilter ?? prev;
      const balances = {};
      activeAccountEntries.forEach(([id, acc]) => {
        if (!['Bank', 'Loan'].includes(acc.type)) {
          return;
        }
        const existing = base?.[id];
        if (existing === undefined) {
          balances[id] = acc.type === 'Bank';
        } else {
          balances[id] = existing;
        }
      });
      if (shallowEqual(prev, balances)) {
        return prev;
      }
      const shouldPersist =
        settingsBalanceFilter === undefined ||
        !shallowEqual(settingsBalanceFilter, balances);
      if (shouldPersist) {
        persistSetting('balanceAccountFilter', balances);
      }
      return balances;
    });
  }, [
    settingsLoaded,
    filtersHydrated,
    activeAccountEntries,
    settingsExpenseFilter,
    settingsIncomeFilter,
    settingsIncomeExpenseFilter,
    settingsCashflowFilter,
    settingsCashShortfallFilter,
    settingsBalanceFilter,
    persistSetting,
  ]);

  const searchText = transactionFilterText.trim().toLowerCase();
  const hasSearch = searchText.length > 0;
  const amountSearchText = normalizeAmountSearch(searchText);
  const hasAmountSearch = /\d/.test(amountSearchText);
  const selectedReconciliationAccountSet = useMemo(
    () => new Set(
      Object.entries(selectedReconciliationAccounts)
        .filter(([, selected]) => Boolean(selected))
        .map(([accountId]) => accountId)
    ),
    [selectedReconciliationAccounts],
  );
  const filteredWidgetTransactions = useMemo(() => {
    const availableTransactions = transactions.filter((t) => (
      !t.isPaid || isTransferAwaitingReconciliation(t, selectedReconciliationAccountSet)
    ));
    if (!hasSearch) return availableTransactions;

    return availableTransactions.filter((transaction) => {
      const description = transaction.description?.toLowerCase() || '';
      const fromAccountName = (accounts[transaction.fromAccount]?.name || '').toLowerCase();
      const toAccountName = (accounts[transaction.toAccount]?.name || '').toLowerCase();
      const amountMatches = hasAmountSearch
        ? (() => {
            const amount = Number(transaction.amount || 0);
            const absoluteAmount = Math.abs(amount);
            const amountText = [
              amount.toString(),
              amount.toFixed(2),
              absoluteAmount.toString(),
              absoluteAmount.toFixed(2),
            ].join(' ');
            return amountText.includes(amountSearchText);
          })()
        : false;

      return (
        description.includes(searchText) ||
        fromAccountName.includes(searchText) ||
        toAccountName.includes(searchText) ||
        amountMatches
      );
    });
  }, [
    accounts,
    amountSearchText,
    hasAmountSearch,
    hasSearch,
    searchText,
    selectedReconciliationAccountSet,
    transactions,
  ]);

  const displayedWidgetTransactions = filteredWidgetTransactions.slice(
    0,
    visibleTransactionCount
  );

  useEffect(() => {
    const loadMoreEl = transactionLoadMoreRef.current;
    const containerEl = transactionListRef.current;
    if (!loadMoreEl || !containerEl) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleTransactionCount((prev) => {
              if (prev >= filteredWidgetTransactions.length) return prev;
              return Math.min(
                prev + TRANSACTION_WIDGET_PAGE_SIZE,
                filteredWidgetTransactions.length
              );
            });
          }
        });
      },
      { root: containerEl, rootMargin: '100px' }
    );

    observer.observe(loadMoreEl);

    return () => observer.disconnect();
  }, [filteredWidgetTransactions.length, transactionFilterText]);

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
      amount
    );

  const formatCurrencyNoCents = (amount) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(amount);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const day = date.getDate();
    const suffix =
      day % 10 === 1 && day !== 11
        ? 'st'
        : day % 10 === 2 && day !== 12
        ? 'nd'
        : day % 10 === 3 && day !== 13
        ? 'rd'
        : 'th';
    const month = date.toLocaleString('en-US', { month: 'short' });
    return `${day}${suffix} ${month}`;
  };

  const formatFullDate = (dateObj) => {
    if (!dateObj) return 'Unknown';
    const date = dateObj instanceof Date ? dateObj : new Date(dateObj);
    if (Number.isNaN(date.getTime())) return 'Unknown';
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(date);
  };

  const formatAccountAvailability = (account) => {
    if (account?.type !== 'Asset') return '';
    if (account.availabilityType === 'age' && account.availableFromAge != null) {
      return ' — available from age ' + account.availableFromAge;
    }
    if (account.availabilityType === 'date' && account.availableFromDate) {
      return ' — available from ' + formatFullDate(account.availableFromDate);
    }
    return ' — available now';
  };

  const selectedCashflowAccounts = useMemo(
    () =>
      activeAccountEntries.filter(
        ([id, acc]) =>
          CASHFLOW_ACCOUNT_TYPES.includes(acc.type) && cashflowAccountFilter[id]
      ),
    [activeAccountEntries, cashflowAccountFilter]
  );

  const selectedCashShortfallAccounts = useMemo(
    () =>
      activeAccountEntries.filter(
        ([id, acc]) =>
          SHORTFALL_ACCOUNT_TYPES.includes(acc.type) && cashShortfallAccountFilter[id]
      ),
    [activeAccountEntries, cashShortfallAccountFilter]
  );

  const cashRunwayInfo = useMemo(() => {
    const relevantAccounts = activeAccountEntries.filter(([, acc]) =>
      SHORTFALL_ACCOUNT_TYPES.includes(acc?.type)
    );

    if (relevantAccounts.length === 0) {
      return { status: 'noAccounts' };
    }
    if (selectedCashShortfallAccounts.length === 0) {
      return { status: 'noSelection' };
    }

    const projection = buildCashflowProjection({
      accounts: selectedCashShortfallAccounts,
      transactions,
      startDate: new Date(),
      birthDate,
      respectAccountAvailability: true,
    });
    const projectionEndDate = projection.lastImpactDate || new Date();

    if (projection.runOutDate) {
      return {
        ...projection,
        status: 'runout',
        runOutAge: calculateAgeOnDate(birthDate, projection.runOutDate),
      };
    }

    return {
      ...projection,
      status: 'safe',
      projectionEndDate,
      projectionAge: calculateAgeOnDate(birthDate, projectionEndDate),
    };
  }, [activeAccountEntries, selectedCashShortfallAccounts, transactions, birthDate]);

  const getOverviewData = () => {
    const projection = buildCashflowProjection({
      accounts: selectedCashflowAccounts,
      transactions,
      startDate: new Date(),
      endDate: addCalendarMonths(new Date(), cashflowPeriod),
    });
    const { labels, accountPoints, totalPoints } = projection;

    const buildSeries = (points) => {
      const data = [];
      let index = 0;
      let last = points[0]?.balance ?? 0;
      labels.forEach((label) => {
        while (index < points.length && points[index].date <= label) {
          last = points[index].balance;
          index += 1;
        }
        data.push(last);
      });
      return data;
    };

    const datasets = selectedCashflowAccounts.map(([id, account]) => ({
      label: account.name,
      data: buildSeries(accountPoints[id]),
      borderColor: account.color || '#4CAF50',
      tension: 0.1,
    }));

    datasets.push({
      label: 'Total',
      data: buildSeries(totalPoints),
      borderColor: '#000000',
      borderWidth: 2,
      tension: 0.1,
    });

    return { labels, datasets };
  };

  const getExpenseData = () => {
    const now = new Date();
    const startOfCurrentMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const startOfNextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    const startOfMonthAfterNext = new Date(now.getFullYear(), now.getMonth() + 2, 1);
    const startOfPreviousMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const monthFormatter = new Intl.DateTimeFormat('en-US', { month: 'short' });

    const dataMap = {};
    let totalPrevious = 0;
    let totalCurrent = 0;
    let totalNext = 0;

    transactions.forEach((t) => {
      const d = new Date(t.date);
      if (Number.isNaN(d.getTime())) return;
      const amt = Math.abs(parseFloat(t.amount));
      if (!Number.isFinite(amt)) return;

      let periodKey = null;
      if (d >= startOfPreviousMonth && d < startOfCurrentMonth) {
        periodKey = 'previous';
      } else if (d >= startOfCurrentMonth && d < startOfNextMonth) {
        periodKey = 'current';
      } else if (d >= startOfNextMonth && d < startOfMonthAfterNext) {
        periodKey = 'next';
      }
      if (!periodKey) return;

      const accId = t.toAccount;
      const acc = accounts[accId];
      if (acc?.type === 'Expense' && expenseAccountFilter[accId]) {
        if (!dataMap[accId]) {
          dataMap[accId] = {
            previous: 0,
            current: 0,
            next: 0,
            color: acc.color || '#c26166',
            label: acc.name,
          };
        }
        dataMap[accId][periodKey] += amt;
        if (periodKey === 'previous') totalPrevious += amt;
        if (periodKey === 'current') totalCurrent += amt;
        if (periodKey === 'next') totalNext += amt;
      }
    });

    const datasets = Object.values(dataMap).map((acc) => ({
      label: acc.label,
      data: [acc.previous, acc.current, acc.next],
      backgroundColor: acc.color,
    }));

    const previousMonthLabel = monthFormatter.format(startOfPreviousMonth);
    const currentMonthLabel = monthFormatter.format(startOfCurrentMonth);
    const nextMonthLabel = monthFormatter.format(startOfNextMonth);

    const labels = [
      [`${previousMonthLabel}`, formatCurrency(totalPrevious)],
      [`${currentMonthLabel}`, formatCurrency(totalCurrent)],
      [`${nextMonthLabel}`, formatCurrency(totalNext)],
    ];

    return { labels, datasets };
  };

  const getIncomeData = () => {
    const now = new Date();
    const startOfCurrentMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const startOfNextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    const startOfMonthAfterNext = new Date(now.getFullYear(), now.getMonth() + 2, 1);
    const startOfPreviousMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const monthFormatter = new Intl.DateTimeFormat('en-US', { month: 'short' });

    const dataMap = {};
    let totalPrevious = 0;
    let totalCurrent = 0;
    let totalNext = 0;

    transactions.forEach((t) => {
      const d = new Date(t.date);
      if (Number.isNaN(d.getTime())) return;
      const amt = Math.abs(parseFloat(t.amount));
      if (!Number.isFinite(amt)) return;

      let periodKey = null;
      if (d >= startOfPreviousMonth && d < startOfCurrentMonth) {
        periodKey = 'previous';
      } else if (d >= startOfCurrentMonth && d < startOfNextMonth) {
        periodKey = 'current';
      } else if (d >= startOfNextMonth && d < startOfMonthAfterNext) {
        periodKey = 'next';
      }
      if (!periodKey) return;

      const accId = t.fromAccount;
      const acc = accounts[accId];
      if (acc?.type === 'Income' && incomeAccountFilter[accId]) {
        if (!dataMap[accId]) {
          dataMap[accId] = {
            previous: 0,
            current: 0,
            next: 0,
            color: acc.color || '#4CAF50',
            label: acc.name,
          };
        }
        dataMap[accId][periodKey] += amt;
        if (periodKey === 'previous') totalPrevious += amt;
        if (periodKey === 'current') totalCurrent += amt;
        if (periodKey === 'next') totalNext += amt;
      }
    });

    const datasets = Object.values(dataMap).map((acc) => ({
      label: acc.label,
      data: [acc.previous, acc.current, acc.next],
      backgroundColor: acc.color,
    }));

    const previousMonthLabel = monthFormatter.format(startOfPreviousMonth);
    const currentMonthLabel = monthFormatter.format(startOfCurrentMonth);
    const nextMonthLabel = monthFormatter.format(startOfNextMonth);

    const labels = [
      [`${previousMonthLabel}`, formatCurrency(totalPrevious)],
      [`${currentMonthLabel}`, formatCurrency(totalCurrent)],
      [`${nextMonthLabel}`, formatCurrency(totalNext)],
    ];

    return { labels, datasets };
  };

  const getIncomeExpenseData = () => {
    const now = new Date();
    const startOfCurrentMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const startOfNextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    const startOfMonthAfterNext = new Date(now.getFullYear(), now.getMonth() + 2, 1);
    const startOfPreviousMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const monthFormatter = new Intl.DateTimeFormat('en-US', { month: 'short' });

    const dataMap = {};
    let totalPreviousIncome = 0;
    let totalPreviousExpense = 0;
    let totalCurrentIncome = 0;
    let totalCurrentExpense = 0;
    let totalNextIncome = 0;
    let totalNextExpense = 0;

    transactions.forEach((t) => {
      const d = new Date(t.date);
      if (Number.isNaN(d.getTime())) return;
      const amt = Math.abs(parseFloat(t.amount));
      if (!Number.isFinite(amt)) return;

      let periodKey = null;
      if (d >= startOfPreviousMonth && d < startOfCurrentMonth) {
        periodKey = 'previous';
      } else if (d >= startOfCurrentMonth && d < startOfNextMonth) {
        periodKey = 'current';
      } else if (d >= startOfNextMonth && d < startOfMonthAfterNext) {
        periodKey = 'next';
      }
      if (!periodKey) return;

      const incomeAcc = accounts[t.fromAccount];
      if (incomeAcc?.type === 'Income' && incomeExpenseAccountFilter[t.fromAccount]) {
        if (!dataMap[t.fromAccount]) {
          dataMap[t.fromAccount] = {
            previousIncome: 0,
            previousExpense: 0,
            currentIncome: 0,
            currentExpense: 0,
            nextIncome: 0,
            nextExpense: 0,
            color: incomeAcc.color || '#4CAF50',
            label: incomeAcc.name,
          };
        }
        const incomeKey = `${periodKey}Income`;
        dataMap[t.fromAccount][incomeKey] += amt;
        if (periodKey === 'previous') totalPreviousIncome += amt;
        if (periodKey === 'current') totalCurrentIncome += amt;
        if (periodKey === 'next') totalNextIncome += amt;
      }

      const expenseAcc = accounts[t.toAccount];
      if (expenseAcc?.type === 'Expense' && incomeExpenseAccountFilter[t.toAccount]) {
        if (!dataMap[t.toAccount]) {
          dataMap[t.toAccount] = {
            previousIncome: 0,
            previousExpense: 0,
            currentIncome: 0,
            currentExpense: 0,
            nextIncome: 0,
            nextExpense: 0,
            color: expenseAcc.color || '#c26166',
            label: expenseAcc.name,
          };
        }
        const expenseKey = `${periodKey}Expense`;
        dataMap[t.toAccount][expenseKey] += amt;
        if (periodKey === 'previous') totalPreviousExpense += amt;
        if (periodKey === 'current') totalCurrentExpense += amt;
        if (periodKey === 'next') totalNextExpense += amt;
      }
    });

    const datasets = Object.values(dataMap).map((acc) => ({
      label: acc.label,
      data: [
        acc.previousIncome,
        acc.previousExpense,
        acc.currentIncome,
        acc.currentExpense,
        acc.nextIncome,
        acc.nextExpense,
      ],
      backgroundColor: acc.color,
    }));

    const previousMonthLabel = monthFormatter.format(startOfPreviousMonth);
    const currentMonthLabel = monthFormatter.format(startOfCurrentMonth);
    const nextMonthLabel = monthFormatter.format(startOfNextMonth);

    const labels = [
      [`${previousMonthLabel} Income`, formatCurrencyNoCents(totalPreviousIncome)],
      [`${previousMonthLabel} Expense`, formatCurrencyNoCents(totalPreviousExpense)],
      [`${currentMonthLabel} Income`, formatCurrencyNoCents(totalCurrentIncome)],
      [`${currentMonthLabel} Expense`, formatCurrencyNoCents(totalCurrentExpense)],
      [`${nextMonthLabel} Income`, formatCurrencyNoCents(totalNextIncome)],
      [`${nextMonthLabel} Expense`, formatCurrencyNoCents(totalNextExpense)],
    ];

    return { labels, datasets };
  };

  const balanceAccounts = activeAccountEntries.filter(([, acc]) =>
    ['Bank', 'Loan'].includes(acc.type)
  );
  const selectedBalanceAccounts = balanceAccounts.filter(([id]) => balanceAccountFilter[id]);
  const expenseAccounts = activeAccountEntries.filter(([, acc]) => acc.type === 'Expense');
  const incomeAccounts = activeAccountEntries.filter(([, acc]) => acc.type === 'Income');
  const cashflowAccounts = activeAccountEntries.filter(([, acc]) => ['Bank', 'Loan', 'Asset'].includes(acc.type));
  const cashRunwayAccounts = useMemo(
    () =>
      activeAccountEntries
        .filter(([, acc]) => SHORTFALL_ACCOUNT_TYPES.includes(acc.type))
        .sort(([, a], [, b]) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base', numeric: true })),
    [activeAccountEntries]
  );

  const renderWidget = (widget) => {
    const { id, width = 1, height = 1 } = widget;
    const spanStyle = { gridColumn: `span ${width}`, gridRow: `span ${height}` };
    switch (id) {
      case 'transactions':
        return (
          <Card key={id} style={spanStyle}>
            <h2 style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Transactions</span>
              <span style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <InputWrapper style={{ width: '150px' }}>
                  <Input
                    placeholder="Search"
                    value={transactionFilterText}
                    onChange={(e) => setTransactionFilterText(e.target.value)}
                  />
                  {transactionFilterText && (
                    <ClearButton onClick={() => setTransactionFilterText('')} aria-label="Clear search">
                      &times;
                    </ClearButton>
                  )}
                </InputWrapper>
                <Button
                  title="Add New Transaction"
                  aria-label="Add new transaction"
                  onClick={handleAddTransactionButtonClick}
                  onDragOver={handleAddTransactionButtonDragOver}
                  onDragEnter={handleAddTransactionButtonDragOver}
                  onDragLeave={handleAddTransactionButtonDragLeave}
                  onDrop={handleAddTransactionButtonDrop}
                  style={isAddTransactionButtonDragOver ? { outline: '2px dashed #4CAF50' } : undefined}
                >
                  +
                </Button>
              </span>
            </h2>
            <CardContent
              ref={transactionListRef}
              style={{ overflowY: 'auto', overflowX: 'visible' }}
            >
              <StyledList>
                {displayedWidgetTransactions.map((t) =>
                  renderReconciliationListItem(t)
                )}
                {displayedWidgetTransactions.length > 0 &&
                  displayedWidgetTransactions.length < filteredWidgetTransactions.length && (
                    <StyledListItem
                      key="transactions-load-more"
                      ref={transactionLoadMoreRef}
                      aria-hidden="true"
                      style={{
                        visibility: 'hidden',
                        padding: 0,
                        borderBottom: 'none',
                        minHeight: '1px',
                        height: '1px',
                        flex: '0 0 auto',
                      }}
                    />
                  )}
                {displayedWidgetTransactions.length === 0 && (
                  <StyledListItem>
                    {hasSearch
                      ? 'No transactions match your search'
                      : 'No unpaid transactions'}
                  </StyledListItem>
                )}
              </StyledList>
            </CardContent>
            {reconDropContext && (
              <ModalContainer onClick={reconProcessing ? undefined : clearReconDropContext}>
                <ModalContent onClick={(e) => e.stopPropagation()}>
                  {reconDropContext.mode === 'recurringOccurrence' ? (
                    <>
                      <h3>Recurring Transaction Match</h3>
                      <p>
                        This looks like a recurring transaction. You dropped the reconciliation on {formatDate(reconDropContext.transaction.date)}, but the first unpaid occurrence is {formatDate(reconDropContext.firstUnpaidOccurrence.date)}.
                      </p>
                      <p>Do you want to mark off the first unpaid occurrence instead?</p>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                        <Button disabled={reconProcessing} onClick={() => handleRecurringOccurrenceDropAction('firstUnpaid')}>
                          Use First Unpaid Occurrence
                        </Button>
                        <Button disabled={reconProcessing} onClick={() => handleRecurringOccurrenceDropAction('dropped')}>
                          Use Dropped Occurrence
                        </Button>
                        <Button disabled={reconProcessing} onClick={() => handleRecurringOccurrenceDropAction('cancel')}>
                          Cancel
                        </Button>
                      </div>
                    </>
                  ) : reconDropContext.mode === 'partial' ? (
                    <>
                      <h3>Partial Reconciliation</h3>
                      <p>
                        Reconciliation amount {formatCurrency(reconDropContext.reconAbs)} is less than the transaction amount {formatCurrency(reconDropContext.transactionAbs)}.
                      </p>
                      <p>How would you like to handle this transaction?</p>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                        <Button disabled={reconProcessing} onClick={() => handleReconDropAction('partial')}>
                          Apply Partial Payment
                        </Button>
                        <Button disabled={reconProcessing} onClick={() => handleReconDropAction('adjust')}>
                          Change Transaction Amount
                        </Button>
                        <Button disabled={reconProcessing} onClick={() => handleReconDropAction('cancel')}>
                          Cancel
                        </Button>
                      </div>
                    </>
                  ) : (
                    <>
                      <h3>Adjust Transaction Amount</h3>
                      <p>
                        Reconciliation amount {formatCurrency(reconDropContext.reconAbs)} exceeds the current transaction amount {formatCurrency(reconDropContext.transactionAbs)}.
                      </p>
                      <p>Do you want to adjust the transaction to match the reconciliation amount?</p>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                        <Button disabled={reconProcessing} onClick={() => handleReconDropAction('adjust')}>
                          Change Transaction Amount
                        </Button>
                        <Button disabled={reconProcessing} onClick={() => handleReconDropAction('cancel')}>
                          Cancel
                        </Button>
                      </div>
                    </>
                  )}
                </ModalContent>
              </ModalContainer>
            )}
          </Card>
        );
      case 'reconciliation':
        return (
          <ReconciliationWidget key={id} accounts={accounts} style={spanStyle} />
        );
      case 'bugReports':
        return <BugReportsWidget key={id} style={spanStyle} isAdmin={isAdmin} />;
      case 'cashRunway': {
        const {
          status,
          finalBalance,
          runOutDate,
          runOutAge,
          projectionEndDate,
          projectionAge,
        } = cashRunwayInfo;
        const timeUntilRunOut = formatTimeUntilDate(new Date(), runOutDate);
        return (
          <Card key={id} style={spanStyle}>
            <h2 style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Projected Cash Shortfall</span>
              {cashRunwayAccounts.length > 0 && (
                <Button onClick={() => setIsCashRunwayModalOpen(true)}>Accounts</Button>
              )}
            </h2>
            <CardContent>
              {status === 'noAccounts' ? (
                <p>Add at least one Bank or Asset account to calculate your shortfall projection.</p>
              ) : status === 'noSelection' ? (
                <p>Select at least one account to include in the shortfall calculation.</p>
              ) : (
                <>
                  {status === 'runout' ? (
                    <>
                      <p><strong>Projected shortfall:</strong> {formatFullDate(runOutDate)}</p>
{timeUntilRunOut && (
                        <p><strong>Time until that date:</strong> {timeUntilRunOut}</p>
                      )}
                      {runOutAge != null && (
                        <p><strong>Age on that date:</strong> {runOutAge}</p>
                      )}
                    </>
                  ) : (
                    <>
                      <p>
                        No projected cash shortfall through {formatFullDate(projectionEndDate)}
                        {projectionAge != null ? ' (age ' + projectionAge + ')' : ''}.
                      </p>
                      <p><strong>Projected balance:</strong> {formatCurrency(finalBalance ?? 0)}</p>
                      <p>Based only on the scheduled transactions available through that date.</p>
                    </>
                  )}
                </>
              )}
            </CardContent>
            {isCashRunwayModalOpen && (
              <ModalContainer onClick={() => setIsCashRunwayModalOpen(false)}>
                <ModalContent onClick={(e) => e.stopPropagation()}>
                  <h3>Projection Accounts</h3>
                  <p>Choose the accounts used for the shortfall projection.</p>
                  {cashRunwayAccounts.length === 0 ? (
                    <p>No eligible accounts available.</p>
                  ) : (
                    <StyledList>
                      {cashRunwayAccounts.map(([accId, acc]) => (
                        <StyledListItem key={accId} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <span>{acc.name}{formatAccountAvailability(acc)}</span>
                          <input
                            type="checkbox"
                            checked={!!cashShortfallAccountFilter[accId]}
                            onChange={() => toggleCashShortfallAccount(accId)}
                            aria-label={'Include ' + acc.name}
                          />
                        </StyledListItem>
                      ))}
                    </StyledList>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px' }}>
                    <Button
                      onClick={() => {
                        const next = {};
                        cashRunwayAccounts.forEach(([accId]) => {
                          next[accId] = true;
                        });
                        setCashShortfallAccountFilter(next);
                        persistSetting('cashShortfallAccountFilter', next);
                      }}
                    >
                      Select All
                    </Button>
                    <Button onClick={() => setIsCashRunwayModalOpen(false)}>Done</Button>
                  </div>
                </ModalContent>
              </ModalContainer>
            )}
          </Card>
        );
      }
      case 'cashflow':
        return (
          <Card key={id} style={spanStyle}>
            <h2>Cashflow</h2>
            <CardContent>
              <div>
                <label>
                  Period:
                  <Select
                    value={cashflowPeriod}
                    onChange={(e) => {
                      const nextPeriod = parseInt(e.target.value, 10);
                      setCashflowPeriod(nextPeriod);
                      persistSetting('cashflowPeriod', nextPeriod);
                    }}
                  >
                    <option value={1}>1 month</option>
                    <option value={3}>3 months</option>
                    <option value={6}>6 months</option>
                    <option value={12}>1 year</option>
                    <option value={24}>2 years</option>
                    <option value={36}>3 years</option>
                    <option value={60}>5 years</option>
                    <option value={120}>10 years</option>
                    <option value={240}>20 years</option>
                    <option value={600}>50 years</option>
                    <option value={1200}>100 years</option>
                  </Select>
                </label>
                <Dropdown>
                  <Button onClick={() => toggleAccountDropdown('cashflow')}>Accounts ▾</Button>
                  {openAccountDropdown === 'cashflow' && (
                    <DropdownContent>
                      {cashflowAccounts.map(([accId, acc]) => (
                        <label key={accId} style={{ display: 'block' }}>
                          <input
                            type="checkbox"
                            checked={cashflowAccountFilter[accId]}
                            onChange={() => toggleCashflowAccount(accId)}
                          />
                          {acc.name}
                        </label>
                      ))}
                    </DropdownContent>
                  )}
                </Dropdown>
              </div>
              <div style={{ position: 'relative', height: '300px', paddingBottom: '20px', boxSizing: 'border-box' }}>
                <Line data={getOverviewData()} options={cashflowChartOptions} />
              </div>
            </CardContent>
          </Card>
        );
      case 'expenses': {
        const expenseData = getExpenseData();
        return (
          <Card key={id} style={spanStyle}>
            <h2>Expenses</h2>
            <CardContent>
              <Dropdown>
                <Button onClick={() => toggleAccountDropdown('expenses')}>Accounts ▾</Button>
                {openAccountDropdown === 'expenses' && (
                  <DropdownContent>
                    {expenseAccounts.map(([accId, acc]) => (
                      <label key={accId} style={{ display: 'block' }}>
                        <input
                          type="checkbox"
                          checked={expenseAccountFilter[accId]}
                          onChange={() => toggleExpenseAccount(accId)}
                        />
                        {acc.name}
                      </label>
                    ))}
                  </DropdownContent>
                )}
              </Dropdown>
              <div style={{ flex: 1 }}>
                <Bar
                  data={expenseData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: { legend: { display: false } },
                    scales: { x: { stacked: true }, y: { stacked: true } }
                  }}
                />
              </div>
            </CardContent>
          </Card>
        );
      }
      case 'income': {
        const incomeData = getIncomeData();
        return (
          <Card key={id} style={spanStyle}>
            <h2>Income</h2>
            <CardContent>
              <Dropdown>
                <Button onClick={() => toggleAccountDropdown('income')}>Accounts ▾</Button>
                {openAccountDropdown === 'income' && (
                  <DropdownContent>
                    {incomeAccounts.map(([accId, acc]) => (
                      <label key={accId} style={{ display: 'block' }}>
                        <input
                          type="checkbox"
                          checked={incomeAccountFilter[accId]}
                          onChange={() => toggleIncomeAccount(accId)}
                        />
                        {acc.name}
                      </label>
                    ))}
                  </DropdownContent>
                )}
              </Dropdown>
              <div style={{ flex: 1 }}>
                <Bar
                  data={incomeData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: { legend: { display: false } },
                    scales: { x: { stacked: true }, y: { stacked: true } }
                  }}
                />
              </div>
            </CardContent>
          </Card>
        );
      }
      case 'incomeExpense': {
        const incomeExpenseData = getIncomeExpenseData();
        return (
          <Card key={id} style={spanStyle}>
            <h2>Income & Expenses</h2>
            <CardContent>
              <Dropdown>
                <Button onClick={() => toggleAccountDropdown('incomeExpense')}>Accounts ▾</Button>
                {openAccountDropdown === 'incomeExpense' && (
                  <DropdownContent>
                    {incomeAccounts.map(([accId, acc]) => (
                      <label key={accId} style={{ display: 'block' }}>
                        <input
                          type="checkbox"
                          checked={incomeExpenseAccountFilter[accId]}
                          onChange={() => toggleIncomeExpenseAccount(accId)}
                        />
                        {acc.name}
                      </label>
                    ))}
                    {expenseAccounts.map(([accId, acc]) => (
                      <label key={accId} style={{ display: 'block' }}>
                        <input
                          type="checkbox"
                          checked={incomeExpenseAccountFilter[accId]}
                          onChange={() => toggleIncomeExpenseAccount(accId)}
                        />
                        {acc.name}
                      </label>
                    ))}
                  </DropdownContent>
                )}
              </Dropdown>
              <div style={{ flex: 1 }}>
                <Bar
                  data={incomeExpenseData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: { legend: { display: false } },
                    scales: { x: { stacked: true }, y: { stacked: true } }
                  }}
                />
              </div>
            </CardContent>
          </Card>
        );
      }
      case 'accountBalances':
        return (
          <Card key={id} style={spanStyle}>
            <h2>Account Balances</h2>
            <CardContent>
              <Dropdown>
                <Button onClick={() => toggleAccountDropdown('accountBalances')}>Accounts ▾</Button>
                {openAccountDropdown === 'accountBalances' && (
                  <DropdownContent>
                    {balanceAccounts.map(([accId, acc]) => (
                      <label key={accId} style={{ display: 'block' }}>
                        <input
                          type="checkbox"
                          checked={balanceAccountFilter[accId]}
                          onChange={() => toggleBalanceAccount(accId)}
                        />
                        {acc.name}
                      </label>
                    ))}
                  </DropdownContent>
                )}
              </Dropdown>
              <StyledList>
                {selectedBalanceAccounts.map(([id, acc]) => (
                  <StyledListItem
                    key={id}
                    $clickable
                    onClick={() => handleEditAccount({ id, ...acc })}
                  >
                    {acc.name} - {formatCurrency(acc.balance)}
                  </StyledListItem>
                ))}
                {selectedBalanceAccounts.length === 0 && (
                  <StyledListItem>No accounts selected</StyledListItem>
                )}
              </StyledList>
            </CardContent>
          </Card>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <EditTransactionModal
        transaction={editingTransaction}
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSave={handleSaveTransaction}
        accounts={accounts}
        userId={auth.currentUser?.uid}
      />
      <DeleteTransactionModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onDelete={handleDeleteTransaction}
        transaction={deletingTransaction}
      />
      <EditAccountModal
        account={editingAccount}
        isOpen={isEditAccountModalOpen}
        onClose={handleCloseAccountModal}
        onSave={handleSaveAccount}
      />
      <PartialPayModal
        isOpen={isPartialPayModalOpen}
        onClose={closePartialPayModal}
        onConfirm={handlePartialPay}
        transaction={partialPayTransactionData}
        initialAmount={pendingReconPartialPayment?.defaultAmount}
        initialDate={pendingReconPartialPayment?.defaultDate}
        initialDescription={partialPayTransactionData?.description}
      />
      <AddTransactionModal
        isOpen={isAddTransactionModalOpen}
        onClose={closeAddTransactionModal}
        onSave={handleAddTransaction}
        accounts={accounts}
        birthDate={birthDate}
        userId={auth.currentUser?.uid}
        initialTransaction={addTransactionPrefill}
      />
      {showConfig && (
        <ModalContainer onClick={() => setShowConfig(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <h2>Widgets</h2>
            <CardContent>
              <StyledList>
                {widgets.map((w, index) => (
                  <WidgetListItem key={w.id}>
                    <input
                      type="checkbox"
                      checked={w.enabled}
                      onChange={() => toggleWidget(w.id)}
                    />
                    <span>{widgetNames[w.id]}</span>
                    <Select
                      aria-label="width"
                      value={w.width}
                      onChange={(e) => updateWidgetSize(w.id, 'width', parseInt(e.target.value))}
                    >
                      <option value={1}>1x width</option>
                      <option value={2}>2x width</option>
                      <option value={3}>3x width</option>
                    </Select>
                    <Select
                      aria-label="height"
                      value={w.height}
                      onChange={(e) => updateWidgetSize(w.id, 'height', parseInt(e.target.value))}
                    >
                      <option value={1}>1x height</option>
                      <option value={2}>2x height</option>
                    </Select>
                    <Button onClick={() => moveWidget(index, -1)}>↑</Button>
                    <Button onClick={() => moveWidget(index, 1)}>↓</Button>
                  </WidgetListItem>
                ))}
              </StyledList>
            </CardContent>
            <Button onClick={() => setShowConfig(false)}>Close</Button>
          </ModalContent>
        </ModalContainer>
      )}
      <DashboardGrid>
        {widgets.filter((w) => w.enabled).map((w) => renderWidget(w))}
      </DashboardGrid>
    </>
  );
};

export default Overview;
